import { useEffect, useMemo } from 'react'

import { ErrorMessage, LoadingDots, Spinner } from '@loadsmart/loadsmart-ui'
import { Dialog, Layout, Text } from '@loadsmart/miranda-react'

import { RequestStatus } from 'core/domain/Request'
import { type Utm } from 'core/domain/Utm'
import { type HttpClientError } from 'core/infra/http/HttpClient'
import { AsyncContent } from 'core/ui/async-content/AsyncContent'

// TODO: Refactor these legacy components to the new architecture
import { useBidDialog } from 'legacy/containers/LoadsPage/BidManager/BidDialog/useBidDialog'
import { UnavailableMessage } from 'legacy/containers/LoadsPage/BookDialog/partials/UnavailableMessage'
import { useAnalytics } from 'legacy/containers/LoadsPage/hooks/useAnalytics'
import { LoadTaken } from 'legacy/containers/LoadsPage/LoadTaken/LoadTaken'
import { getLoadPrice } from 'legacy/containers/LoadsPage/utils/getLoadPrice'

import { createPreferredLane } from 'preferred-lanes/domain/PreferredLane'
import { useSuggestedPreferredLanesContext } from 'preferred-lanes/ui/context/suggestedPreferredLanesContext'
import { useSuggestedPreferredLanes } from 'preferred-lanes/ui/hooks/useSuggestedPreferredLanes'

import { shouldRemoveBidOnBookingComplete, isBidAwarded } from '../../domain/Bid'
import {
  type BookingOffer,
  getLoadOfferType,
  type OfferType,
  BookingType,
} from '../../domain/LoadOffer'
import {
  getHeaderTitle,
  getSubtitle,
  getConfirmButtonLabel,
  getCancelButtonLabel,
} from '../../presenters/bookConfirmationPresenter'
import { getLaneDescription } from '../../presenters/lanePresenter'
import { useAcceptBid } from '../../ui/hooks/useAcceptBid'
import { useActiveBid } from '../../ui/hooks/useActiveBid'
import { useConfirmBook } from '../hooks/useConfirmBook'
import { useLoadOffer } from '../hooks/useLoadOffer'
import { LoadSummary } from '../load-summary/LoadSummary'

export type BookConfirmationDialogProps = {
  loadOfferId: string
  bookingOffer?: BookingOffer
  utmParams: Utm
  onConfirm: (offerType: OfferType) => void
  onCancel: () => void
  bookingType?: BookingType
}

export function BookConfirmationDialog({
  loadOfferId,
  utmParams,
  bookingOffer,
  onConfirm,
  onCancel,
  bookingType = BookingType.BookNow,
}: BookConfirmationDialogProps) {
  const { analytics } = useAnalytics()
  const { fetchLoadOfferStatus, fetchLoadOfferByLoadId, loadOffer } = useLoadOffer()
  const { isConfirming, confirmBookStatus, bookingErrorCode, confirmBook } = useConfirmBook()
  const { acceptBid, isAccepting, acceptedBidErrorCode } = useAcceptBid()
  const { openDialog: openBidDialog, removeBid, refuseBid } = useBidDialog()

  const { setPreferredLaneSuggestions } = useSuggestedPreferredLanesContext()
  const { trackLaneBook } = useSuggestedPreferredLanes()
  const activeBid = useActiveBid(bookingOffer?.id ? { uuid: bookingOffer?.id } : undefined)

  const lane = useMemo(() => {
    if (loadOffer) {
      const preferredLane = createPreferredLane(loadOffer.stops, loadOffer.equipmentType)
      return {
        ...preferredLane,
        // To make it compatible with legacy code
        equipment_types: preferredLane.equipmentTypes,
      }
    }
  }, [loadOffer])

  const isLoadTaken = loadOffer?.activeOffer == null
  const hasBookingEligilityIssue = bookingErrorCode === 403 || bookingErrorCode === 409
  const hasBidAcceptantceEligilityIssue =
    acceptedBidErrorCode === 403 || acceptedBidErrorCode === 409
  const isEligibilityIssue = hasBookingEligilityIssue || hasBidAcceptantceEligilityIssue
  const isFetchingLoadOffer = fetchLoadOfferStatus === RequestStatus.Pending
  const isReadyToDisplay = fetchLoadOfferStatus === RequestStatus.Done
  const hasFetchError = fetchLoadOfferStatus === RequestStatus.Error
  const hasConfirmBookError = confirmBookStatus === RequestStatus.Error
  const hasError = hasFetchError || hasConfirmBookError || typeof acceptedBidErrorCode === 'number'
  const isLoading = isFetchingLoadOffer || isConfirming || isAccepting
  const shouldDisplayActions = isReadyToDisplay && loadOffer && !isLoadTaken && !hasError
  const offerType = getLoadOfferType(loadOffer)
  const isActiveBidAwarded = isBidAwarded(activeBid)

  function handleBook() {
    const { activeOffer } = loadOffer!
    const { uuid: offerUUID } = activeOffer!
    return confirmBook({ loadOfferUUID: loadOfferId, offerUUID, utmParams })
  }

  function handleAccept() {
    return acceptBid(activeBid!.uuid)
  }

  function handleBookingComplete() {
    if (shouldRemoveBidOnBookingComplete(bookingType)) {
      removeBid(activeBid)
    }
  }

  /**
   * If there's and awarded bid we're going to accept the bid for the user instead of booking
   */
  async function handleConfirm() {
    const confirmFn = isActiveBidAwarded ? handleAccept : handleBook
    try {
      await confirmFn()
      const suggestions = await trackLaneBook(lane!)
      setPreferredLaneSuggestions(suggestions)
      const { activeOffer, mileage, shipperMarket, mode } = loadOffer!
      const { uuid: offerUUID } = activeOffer!

      const { triggerContext = 'URL', position = 0 } = bookingOffer || {}
      const pricingMethod = loadOffer!.activeOffer?.pricingMethod ?? 'unknown'
      const price = `${loadOffer!.activeOffer?.price}`
      const laneDescription = getLaneDescription(loadOffer!)

      const eventAction = isActiveBidAwarded ? 'Accept Awarded Bid' : 'Confirm'
      analytics.track(['Book Dialog', 'Confirmation Step', eventAction], {
        lane: laneDescription,
        loadUUID: loadOfferId,
        loadMode: mode,
        mileage,
        offerType,
        offerUUID,
        position,
        price,
        pricingMethod,
        shipperMarket,
        triggerContext,
        bidPrice: isActiveBidAwarded ? `${getLoadPrice(activeBid, loadOffer!)}` : null,
      })

      const tag = isActiveBidAwarded ? 'Awarded Bid Accepted' : 'Booked'
      analytics.tag([tag, offerType])
      analytics.trackIntercom(['ASL'])

      onConfirm(offerType)
      handleBookingComplete()
    } catch (e) {
      const error = e as HttpClientError<{ detail: string }>
      const errorTag = isActiveBidAwarded ? 'Awarded Bid Accept Error' : 'Book Error'
      analytics.tag([errorTag, offerType])

      const errorTrackTrigger = isActiveBidAwarded ? 'Awarded Bid Accept Error' : 'Confirm Error'
      analytics.track(['Book Dialog', 'Confirmation Step', errorTrackTrigger], {
        description: error.response?.data?.detail,
        status: error.response?.status,
      })
    }
  }

  function handleClose() {
    analytics.track(['Book Dialog', 'Closed'])
    onCancel()
    handleBookingComplete()
  }

  function handleCancel() {
    handleClose()

    switch (bookingType) {
      case BookingType.BidAward:
      case BookingType.BidCounter:
        return refuseBid(activeBid)
      case BookingType.BidTooHigh:
        return openBidDialog(loadOffer)
    }
  }

  useEffect(() => {
    const abortController = new AbortController()

    fetchLoadOfferByLoadId(loadOfferId, abortController.signal)

    return () => {
      abortController.abort()
    }
  }, [loadOfferId, fetchLoadOfferByLoadId])

  let content

  if (hasConfirmBookError) {
    content = <UnavailableMessage isEligibilityIssue={isEligibilityIssue} lane={lane} />
  } else if (isReadyToDisplay) {
    if (!loadOffer) {
      content = <LoadTaken />
    } else if (isLoadTaken) {
      content = <LoadTaken lane={lane} userIsLoggedIn />
    } else {
      content = (
        <>
          <Layout.Box paddingT="none" paddingL="none">
            <Text variant="body-md" color="color-text-tertiary">
              {getSubtitle(bookingType, activeBid)}
            </Text>
          </Layout.Box>
          <LoadSummary
            bookingType={bookingType}
            loadOffer={loadOffer}
            price={getLoadPrice(activeBid, loadOffer)}
            bid={activeBid}
          />
        </>
      )
    }
  }

  return (
    <Dialog onClose={handleClose} open>
      <Dialog.Header>
        {getHeaderTitle(bookingType)}
        <Dialog.Close data-testid="close-button" />
      </Dialog.Header>
      <Dialog.Body>
        <AsyncContent
          status={fetchLoadOfferStatus}
          loadingState={<Spinner size={48} />}
          errorState={<ErrorMessage>Failed to load the booking info</ErrorMessage>}
        >
          {content}
        </AsyncContent>
      </Dialog.Body>
      {shouldDisplayActions && (
        <Dialog.Actions>
          <Dialog.ActionSecondary onClick={handleCancel}>
            {getCancelButtonLabel(bookingType)}
          </Dialog.ActionSecondary>
          <Dialog.ActionPrimary onClick={handleConfirm} disabled={isLoading}>
            {isLoading && <LoadingDots variant="light" />}
            {!isLoading && getConfirmButtonLabel(bookingType)}
          </Dialog.ActionPrimary>
        </Dialog.Actions>
      )}
    </Dialog>
  )
}
